// Side-effect-only component: watches telemetry transitions, fires toasts.
// Renders nothing. Mounted at the app root in __root.tsx as a sibling of
// TelemetryProvider's children (not inside the provider) so the provider
// stays pure. The ToastAction wrappers below live in this file because
// toast({ action }) requires a React.ReactElement<typeof ToastAction>.
import { useNavigate } from '@tanstack/react-router';

import { VersionStatus } from '@/api/types/node';
import { ToastAction } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { CodebaseTransition, useStatusTransitions } from '@/contexts/TelemetryContext';
import { useContextMode } from '@/hooks/useContextMode';
import { useUser } from '@/hooks/useUser';

function GenerateNowAction({ codebaseId, organizationName }: { codebaseId: string; organizationName: string }) {
  const navigate = useNavigate();
  return (
    <ToastAction
      altText="Generate context now"
      onClick={() =>
        navigate({
          params: { organizationName },
          search: (prev) => ({ ...prev, generateRowIds: [codebaseId] }),
          to: '/$organizationName/library',
        })
      }
    >
      Generate now
    </ToastAction>
  );
}

function ViewCodebaseAction({ codebaseId, organizationName }: { codebaseId: string; organizationName: string }) {
  const navigate = useNavigate();
  return (
    <ToastAction
      altText="View codebase"
      onClick={() =>
        navigate({
          params: { codebaseId, organizationName },
          to: '/$organizationName/cb/$codebaseId/settings/general',
        })
      }
    >
      View
    </ToastAction>
  );
}

function ViewSourcesAction({ organizationName }: { organizationName: string }) {
  const navigate = useNavigate();
  return (
    <ToastAction
      altText="View codebase error details"
      onClick={() =>
        navigate({
          params: { organizationName },
          to: '/$organizationName/sources',
        })
      }
    >
      View details
    </ToastAction>
  );
}

export function TelemetryNotifier(): null {
  const { toast } = useToast();
  const { routeOrganizationName } = useUser();
  // In LITE a connected codebase is as ready as it gets: nothing generates,
  // so the connect toast neither promises context nor offers to start it.
  const isContextLight = useContextMode() === 'LITE';

  // No useCallback — useStatusTransitions captures the latest callback in a
  // ref, so memoization here would have no effect on subscribe identity or
  // re-renders.
  const handleTransition = (t: CodebaseTransition) => {
    // Skip initial observation — the user only sees a toast for transitions
    // that happened during their active session, not for state they walked
    // into via F5/login.
    if (t.fromStatus === null) return;
    if (!routeOrganizationName) return; // Skip if not on org-scoped route

    if (t.toStatus === VersionStatus.CONNECTED && isContextLight) {
      toast({
        id: `codebase-ready-${t.codebaseId}`,
        title: `${t.codebaseName} is connected`,
      });
    } else if (t.toStatus === VersionStatus.CONNECTED) {
      toast({
        action: <GenerateNowAction codebaseId={t.codebaseId} organizationName={routeOrganizationName} />,
        description: 'Click below to start generating.',
        id: `codebase-ready-${t.codebaseId}`,
        title: `${t.codebaseName} is ready to generate context`,
      });
    } else if (t.toStatus === VersionStatus.GENERATION_COMPLETE) {
      toast({
        action: <ViewCodebaseAction codebaseId={t.codebaseId} organizationName={routeOrganizationName} />,
        description: 'Context generation is complete.',
        id: `codebase-complete-${t.codebaseId}`,
        title: `${t.codebaseName} is ready to use`,
      });
    } else if (t.toStatus === VersionStatus.CONNECTION_FAILED) {
      toast({
        action: <ViewSourcesAction organizationName={routeOrganizationName} />,
        description: 'Review the error details in Sources.',
        id: `codebase-failed-${t.codebaseId}`,
        title: `${t.codebaseName} setup failed`,
        variant: 'destructive',
      });
    } else if (t.toStatus === VersionStatus.GENERATION_ERROR) {
      toast({
        id: `codebase-generation-error-${t.codebaseId}`,
        title: `${t.codebaseName} failed to generate context`,
        variant: 'destructive',
      });
    }
  };

  useStatusTransitions(handleTransition);
  return null;
}
